import React, { lazy, Suspense, ComponentType } from 'react';
import { Skeleton } from './ui/skeleton';
import { Loader2 } from 'lucide-react';
// Loading component for lazy routes
export function RouteLoader() {
  return (
    <div className='flex flex-col items-center justify-center min-h-screen p-8'>
      <Loader2 className='h-8 w-8 animate-spin text-primary mb-4' />
      <p className='text-muted-foreground'>Carregando...</p>
      {/* Skeleton preview of page structure */}
      <div className='w-full max-w-4xl mt-8 space-y-4'>
        <Skeleton className='h-12 w-3/4 mx-auto' />
        <Skeleton className='h-64 w-full' />
        <div className='grid grid-cols-3 gap-4'>
          <Skeleton className='h-32' />
          <Skeleton className='h-32' />
          <Skeleton className='h-32' />
        </div>
      </div>
    </div>
  );
}
// Error boundary for failed lazy loads
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}
class LazyLoadErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Lazy load error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className='flex flex-col items-center justify-center min-h-screen p-8'>
          <div className='text-center max-w-md'>
            <h2 className='text-2xl font-bold text-destructive mb-4'>Erro ao carregar página</h2>
            <p className='text-muted-foreground mb-6'>
              Ocorreu um erro ao carregar esta página. Por favor, tente novamente.
            </p>
            <button
              onClick={() => window.location.reload()}
              className='px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors'
            >
              Recarregar Página
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
// Wrapper for lazy loaded components
export function LazyRoute({ children }: { children: React.ReactNode }) {
  return (
    <LazyLoadErrorBoundary>
      <Suspense fallback={<RouteLoader />}>{children}</Suspense>
    </LazyLoadErrorBoundary>
  );
}
// Utility function to create lazy routes with retry logic
export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  retries = 3,
  delay = 1000
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError;
    for (let i = 0; i < retries; i++) {
      try {
        return await componentImport();
      } catch (error) {
        lastError = error;
        console.error('Component loading failed, retrying...', error);
        if (i < retries - 1) {
          await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
        }
      }
    }
    throw lastError;
  });
}
// Preload function for critical routes
export function preloadRoute(componentImport: () => Promise<{ default: ComponentType<any> }>) {
  // Start loading the component in the background
  componentImport().catch(error => {
    console.error('Failed to preload route:', error);
  });
}
// Hook to preload routes on hover/focus
import { useCallback } from 'react';
export function useRoutePreloader() {
  const preload = useCallback((componentImport: () => Promise<{ default: ComponentType<any> }>) => {
    let timeoutId: NodeJS.Timeout;
    const handleInteraction = () => {
      // Delay preload slightly to avoid loading on accidental hover
      timeoutId = setTimeout(() => {
        preloadRoute(componentImport);
      }, 200) as any;
    };
    const handleLeave = () => {
      clearTimeout(timeoutId);
    };
    return {
      onMouseEnter: handleInteraction,
      onFocus: handleInteraction,
      onMouseLeave: handleLeave,
      onBlur: handleLeave,
    };
  }, []);
  return preload;
}
